/**
  Author: Suneel Singh - suneel.singh@enabledata.com
  Purpose: Procedure api models
  Date: April 2020
**/

import { action, thunk } from 'easy-peasy';
import * as proceduresService from '../../services/procedures';
var tempProcedureProgramAssociation = [];
var tempProcedureLoBAssociation = [];
var tempProcedureGroupAssociation = [];
var tempProcedurePolicyAssociation = [];
var tempProcedureClientAssociation = [];
var procedureInfoList = [];

// IH-4438: bound auto-pagination so a runaway dataset can't block the UI.
// When the cap is hit the existing manual refresh takes over.
const MAX_AUTO_PAGES = 20;
// Module-scoped request ID lets in-flight recursive pages know they have been
// superseded by a newer user-initiated fetch (e.g. manual refresh) and bail out.
var procListRequestId = 0;

const proceduresModel = {
  procedures: {},
  isProcedureInfoLoaded: false,

  setIsProcedureInfoLoaded: action((state, payload) => {
    state.isProcedureInfoLoaded = payload;
  }),

  setProcedures: action((state, payload) => {
    state.procedures = payload;
  }),
  getProcedures: thunk(async (actions, payload) => {
    const response = await proceduresService.getProcedures(payload);
    if (response) {
      actions.setProcedures(response.data.data);
      return response.data.data;
    }
  }),
  proceduresList: [],
  setProceduresList: action((state, payload) => {
    state.proceduresList = payload;
  }),
  proceduresListLoader: false,
  setProceduresListLoader: action((state, payload) => {
    state.proceduresListLoader = payload;
  }),
  // IH-4438: exposes the pagination cursor so JSX can grey "Sync" when exhausted.
  // capReached=true means there is more data but we hit MAX_AUTO_PAGES — manual
  // refresh remains active as an escape hatch. hasFetched distinguishes initial
  // mount (still loading first page) from a settled exhausted cursor.
  proceduresPagination: { next_page: null, capReached: false, hasFetched: false },
  setProceduresPagination: action((state, payload) => {
    state.proceduresPagination = payload;
  }),
  getProceduresList: thunk(async (actions, payload, helpers) => {
    // IH-4438: stop recursive call once page route changed.
    if (window.location.pathname.includes('/procedures') === false) {
      return false;
    }
    if (!payload?.next_page) {
      procListRequestId += 1;
      payload = { ...(payload || {}), requestId: procListRequestId, iterations: 0 };
    } else if (payload.requestId === undefined) {
      // Manual refresh inherits the current requestId so the race guard accepts it.
      payload = { ...payload, requestId: procListRequestId };
    }
    const fetchRequestId = payload.requestId;
    const state = helpers.getState();
    return await proceduresService
      .getProceduresList(payload)
      .then(async function (response) {
        if (fetchRequestId !== procListRequestId) {
          // A newer user-initiated fetch has started; abandon this page.
          return response;
        }
        let procedureInfoList = response.data.data.items;
        actions.setProceduresListLoader(false);
        let newProceduresList = state.proceduresList;
        actions.setProceduresList([...newProceduresList, ...procedureInfoList]);
        const nextPage = response.data && response.data.data && response.data.data.pagination && response.data.data.pagination.next_page;
        const nextIteration = (payload.iterations || 0) + 1;
        if (nextPage && nextIteration < MAX_AUTO_PAGES) {
          // Await the recursion so the outermost caller resolves with the final page's response.
          return await actions.getProceduresList({
            active: payload.active,
            next_page: nextPage,
            requestId: fetchRequestId,
            iterations: nextIteration,
          });
        }
        actions.setProceduresPagination({ next_page: nextPage || null, capReached: !!nextPage, hasFetched: true });
        return response;
      })
      .catch(function (error) {
        console.error(error);
        procedureInfoList = [];
        actions.setProceduresList([]);
        return error;
      }).finally(()=>{
        actions.setProceduresListLoader(false);
      });
  }),
  addProcedures: thunk(async (actions, payload) => {
    const response = await proceduresService.addProcedures(payload);
    procedureInfoList.push(response.data.data);
    return response;
  }),
  updateProcedures: thunk(async (actions, payload) => {
    const response = await proceduresService.updateProcedures(payload);
    for (let i = 0; i < procedureInfoList.length; i++) {
      if (procedureInfoList[i].PK === payload.PK) {
        procedureInfoList[i] = response.data.data;
      }
    }
    actions.setProceduresList(procedureInfoList);
    return response;
  }),
  association: {
    programs: {
      items: [],
      loader: false,
    },
    businesslines: {
      items: [],
      loader: false,
    },
    groups: {
      items: [],
      loader: false,
    },
    policies: {
      items: [],
      loader: false,
    },
    clients: {
      items: [],
      loader: false,
    },
  },

  //Association List Procedure -> clients
  setProcedureClientAssociationLoader: action((state, payload) => {
    state.association.clients.loader = payload;
  }),
  setProcedureClientAssociation: action((state, payload) => {
    state.association.clients.items = payload;
  }),
  getProcedureClientAssociation: thunk(async (actions, payload) => {
    actions.setProcedureClientAssociationLoader(true);
    // stop recursive call once page route will changed
    if (window.location.pathname.includes('/procedures') == false) {
      return false;
    }
    return await proceduresService
      .getProcedureClientAssociation(payload)
      .then(function (response) {
        if (payload.repeatAPI === true) {
          tempProcedureClientAssociation =
            tempProcedureClientAssociation.concat(
              response.data.data.associations,
            );
        } else {
          tempProcedureClientAssociation = response.data.data.associations;
        }
        actions.setProcedureClientAssociation(tempProcedureClientAssociation);
        if (response.data.data.lastEvalKeyNew) {
          payload = {
            id: payload.id,
            type: payload.type,
            with: payload.with,
            lastEvalKeyNew: response.data.data.lastEvalKeyNew,
            repeatAPI: true,
          };
          actions.getProcedureClientAssociation(payload);
        } else {
          actions.setProcedureClientAssociationLoader(false);
        }
        return response;
      })
      .catch(function (error) {
        actions.setProcedureClientAssociation([]);
        return error;
      });
  }),
  // Asso List Procedure LoB
  setProcedureLoBAssociationLoader: action((state, payload) => {
    state.association.businesslines.loader = payload;
  }),
  setProcedureLoBAssociation: action((state, payload) => {
    state.association.businesslines.items = payload;
  }),
  getProcedureLoBAssociation: thunk(async (actions, payload) => {
    actions.setProcedureLoBAssociationLoader(true);
    // stop recursive call once page route will changed
    if (window.location.pathname.includes('/procedures') == false) {
      return false;
    }
    return await proceduresService
      .getProcedureLoBAssociation(payload)
      .then(function (response) {
        if (payload.repeatAPI === true) {
          tempProcedureLoBAssociation = tempProcedureLoBAssociation.concat(
            response.data.data.associations,
          );
        } else {
          tempProcedureLoBAssociation = response.data.data.associations;
        }
        actions.setProcedureLoBAssociation(tempProcedureLoBAssociation);
        if (response.data.data.lastEvalKeyNew) {
          payload = {
            id: payload.id,
            type: payload.type,
            with: payload.with,
            lastEvalKeyNew: response.data.data.lastEvalKeyNew,
            repeatAPI: true,
          };
          actions.getProcedureLoBAssociation(payload);
        } else {
          actions.setProcedureLoBAssociationLoader(false);
        }
        return response;
      })
      .catch(function (error) {
        actions.setProcedureLoBAssociation([]);
        return error;
      });
  }),
  // Asso List Procedure Group
  setProcedureGroupAssociationLoader: action((state, payload) => {
    state.association.groups.loader = payload;
  }),
  setProcedureGroupAssociation: action((state, payload) => {
    state.association.groups.items = payload;
  }),
  getProcedureGroupAssociation: thunk(async (actions, payload) => {
    actions.setProcedureGroupAssociationLoader(true);
    // stop recursive call once page route will changed
    if (window.location.pathname.includes('/procedures') == false) {
      return false;
    }
    return await proceduresService
      .getProcedureGroupAssociation(payload)
      .then(function (response) {
        if (payload.repeatAPI === true) {
          tempProcedureGroupAssociation = tempProcedureGroupAssociation.concat(
            response.data.data.associations,
          );
        } else {
          tempProcedureGroupAssociation = response.data.data.associations;
        }
        actions.setProcedureGroupAssociation(tempProcedureGroupAssociation);
        if (response.data.data.lastEvalKeyNew) {
          payload = {
            id: payload.id,
            type: payload.type,
            with: payload.with,
            lastEvalKeyNew: response.data.data.lastEvalKeyNew,
            repeatAPI: true,
          };
          actions.getProcedureGroupAssociation(payload);
        } else {
          actions.setProcedureGroupAssociationLoader(false);
        }
        return response;
      })
      .catch(function (error) {
        actions.setProcedureGroupAssociation([]);
        return error;
      });
  }),
  // Asso List Procedure Program
  setProcedureProgramAssociationLoader: action((state, payload) => {
    state.association.programs.loader = payload;
  }),
  setProcedureProgramAssociation: action((state, payload) => {
    state.association.programs.items = payload;
  }),
  getProcedureProgramAssociation: thunk(async (actions, payload) => {
    actions.setProcedureProgramAssociationLoader(true);
    // stop recursive call once page route will changed
    if (window.location.pathname.includes('/procedures') == false) {
      return false;
    }
    return await proceduresService
      .getProcedureProgramAssociation(payload)
      .then(function (response) {
        if (payload.repeatAPI === true) {
          tempProcedureProgramAssociation =
            tempProcedureProgramAssociation.concat(
              response.data.data.associations,
            );
        } else {
          tempProcedureProgramAssociation = response.data.data.associations;
        }
        actions.setProcedureProgramAssociation(tempProcedureProgramAssociation);
        if (response.data.data.lastEvalKeyNew) {
          payload = {
            id: payload.id,
            type: payload.type,
            with: payload.with,
            lastEvalKeyNew: response.data.data.lastEvalKeyNew,
            repeatAPI: true,
          };
          actions.getProcedureProgramAssociation(payload);
        } else {
          actions.setProcedureProgramAssociationLoader(false);
        }
        return response;
      })
      .catch(function (error) {
        actions.setProcedureProgramAssociation([]);
        return error;
      });
  }),
  // Asso List Procedure Policy
  setProcedurePolicyAssociationLoader: action((state, payload) => {
    state.association.policies.loader = payload;
  }),
  setProcedurePolicyAssociation: action((state, payload) => {
    state.association.policies.items = payload;
  }),
  getProcedurePolicyAssociation: thunk(async (actions, payload) => {
    actions.setProcedurePolicyAssociationLoader(true);
    // stop recursive call once page route will changed
    if (window.location.pathname.includes('/procedures') == false) {
      return false;
    }
    return await proceduresService
      .getProcedurePolicyAssociation(payload)
      .then(function (response) {
        if (payload.repeatAPI === true) {
          tempProcedurePolicyAssociation =
            tempProcedurePolicyAssociation.concat(
              response.data.data.associations,
            );
        } else {
          tempProcedurePolicyAssociation = response.data.data.associations;
        }
        actions.setProcedurePolicyAssociation(tempProcedurePolicyAssociation);
        if (response.data.data.lastEvalKeyNew) {
          payload = {
            id: payload.id,
            type: payload.type,
            with: payload.with,
            lastEvalKeyNew: response.data.data.lastEvalKeyNew,
            repeatAPI: true,
          };
          actions.getProcedurePolicyAssociation(payload);
        } else {
          actions.setProcedurePolicyAssociationLoader(false);
        }
        return response;
      })
      .catch(function (error) {
        actions.setProcedurePolicyAssociation([]);
        return error;
      });
  }),
  updateProcedureStatus: thunk(async (actions, payload) => {
    return await proceduresService.updateProcedureStatus(payload);
  }),
};

export default proceduresModel;
